import { FormEvent, useEffect, useMemo, useState } from 'react';
import { EventCatalogs, EventFormat, NewsletterFormValues } from '../types';
import { mockBackend } from '../services/mockBackend';
import { userFacingError } from '../utils/errors';

type NewsletterPanelProps = {
  catalogs: EventCatalogs;
};

const topicOptions = ['GovTech', 'Capacitação', 'Comunidades'];
const formatOptions: EventFormat[] = ['presencial', 'online', 'hibrido'];

const NewsletterPanel = ({ catalogs }: NewsletterPanelProps) => {
  const [campaigns, setCampaigns] = useState<{ id: string; title: string; sentAt: string; status: string }[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [campaignError, setCampaignError] = useState('');
  const [form, setForm] = useState<NewsletterFormValues>({
    email: '',
    topics: ['GovTech'],
    institutions: [],
    formats: ['presencial', 'hibrido'],
    frequency: 'monthly',
  });

  useEffect(() => {
    mockBackend.newsletter
      .listMockCampaigns()
      .then(setCampaigns)
      .catch((loadError) =>
        setCampaignError(userFacingError(loadError, 'Não foi possível carregar campanhas recentes.'))
      );
  }, []);

  const latestCampaign = useMemo(() => campaigns[0], [campaigns]);

  const toggleValue = (collection: string[], value: string) =>
    collection.includes(value)
      ? collection.filter((item) => item !== value)
      : [...collection, value];

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');

    try {
      await mockBackend.newsletter.subscribe(form);
      setSuccess('Inscrição confirmada. Suas preferências foram salvas com sucesso.');
      setForm((current) => ({ ...current, email: '' }));
    } catch (submissionError) {
      setError(userFacingError(submissionError, 'Não foi possível salvar a inscrição.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card bg-onyx border-0 shadow-lg">
      <div className="card-body p-4">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
          <div>
            <h5 className="text-white mb-1">Newsletter</h5>
            <p className="text-white-50 mb-0">
              Receba resumos da agenda e alertas relevantes direto a partir do backend.
            </p>
          </div>
          {latestCampaign && (
            <span className="badge bg-brand text-white">
              {latestCampaign.status === 'sent' ? 'Último envio' : 'Próximo envio'}
            </span>
          )}
        </div>

        {latestCampaign && (
          <div className="small text-white-50 mb-3">
            <strong className="text-white">{latestCampaign.title}</strong> ·{' '}
            {new Date(latestCampaign.sentAt).toLocaleDateString('pt-BR')}
          </div>
        )}

        {success && <div className="alert alert-success py-2">{success}</div>}
        {error && <div className="alert alert-danger py-2">{error}</div>}
        {campaignError && <div className="alert alert-warning py-2">{campaignError}</div>}

        <form className="row g-3" onSubmit={handleSubmit}>
          <div className="col-12">
            <label className="form-label text-white" htmlFor="newsletter-email">
              E-mail
            </label>
            <input
              id="newsletter-email"
              className="form-control input-contrast"
              type="email"
              placeholder="voce@exemplo.com"
              value={form.email}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              required
            />
          </div>

          <div className="col-12">
            <label className="form-label text-white d-block">Temas</label>
            <div className="d-flex flex-wrap gap-2">
              {topicOptions.map((topic) => (
                <button
                  key={topic}
                  type="button"
                  className={`btn btn-sm ${
                    form.topics.includes(topic) ? 'btn-brand' : 'btn-outline-light'
                  }`}
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      topics: toggleValue(current.topics, topic),
                    }))
                  }
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>

          <div className="col-12">
            <label className="form-label text-white d-block">Formatos</label>
            <div className="d-flex flex-wrap gap-2">
              {formatOptions.map((format) => (
                <button
                  key={format}
                  type="button"
                  className={`btn btn-sm ${
                    form.formats.includes(format) ? 'btn-brand' : 'btn-outline-light'
                  }`}
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      formats: toggleValue(current.formats, format) as EventFormat[],
                    }))
                  }
                >
                  {format}
                </button>
              ))}
            </div>
          </div>

          <div className="col-md-7">
            <label className="form-label text-white" htmlFor="newsletter-institution">
              Instituição prioritária
            </label>
            <select
              id="newsletter-institution"
              className="form-select input-contrast"
              value={form.institutions[0] || ''}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  institutions: event.target.value ? [event.target.value] : [],
                }))
              }
            >
              <option value="">Todas</option>
              {catalogs.institutions.map((institution) => (
                <option key={institution} value={institution}>
                  {institution}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-5">
            <label className="form-label text-white" htmlFor="newsletter-frequency">
              Frequência
            </label>
            <select
              id="newsletter-frequency"
              className="form-select input-contrast"
              value={form.frequency}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  frequency: event.target.value as NewsletterFormValues['frequency'],
                }))
              }
            >
              <option value="weekly">Semanal</option>
              <option value="monthly">Mensal</option>
            </select>
          </div>

          <div className="col-12">
            <button type="submit" className="btn btn-brand w-100" disabled={loading}>
              {loading ? 'Inscrevendo...' : 'Quero receber a agenda'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsletterPanel;
